// Style utilities for dynamic theme management
// Following React + Tailwind + Redux best practices

/**
 * Convert hex color to RGB values for CSS variables
 * Supports 3-digit and 6-digit hex codes
 */
export function hexToRgb(hex: string): string {
  if (!hex || typeof hex !== 'string') return '';
  
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Handle 3-digit hex codes (e.g., #f0f -> #f0f0f0)
  const fullHex = cleanHex.length === 3 
    ? cleanHex.split('').map(char => char + char).join('')
    : cleanHex;
  
  // Convert to RGB
  const bigint = parseInt(fullHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  
  return `${r}, ${g}, ${b}`;
}

/**
 * Set CSS custom property on document root
 * Optimized to avoid unnecessary DOM writes
 */
export function setCssVar(name: string, value: string): void {
  const root = document.documentElement;
  const currentValue = root.style.getPropertyValue(name);
  
  // Only update if value has changed to avoid unnecessary DOM writes
  if (currentValue !== value) {
    root.style.setProperty(name, value);
  }
}

/**
 * Get current CSS custom property value
 */
export function getCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Darken a hex color by a specified amount
 * Useful for hover states and color variations
 */
export function darkenColor(hex: string, amount: number = 30): string {
  if (!hex || typeof hex !== 'string') return '';
  
  const cleanHex = hex.replace('#', '');
  const fullHex = cleanHex.length === 3 
    ? cleanHex.split('').map(char => char + char).join('')
    : cleanHex;
  
  const bigint = parseInt(fullHex, 16);
  const r = Math.max(0, ((bigint >> 16) & 255) - amount);
  const g = Math.max(0, ((bigint >> 8) & 255) - amount);
  const b = Math.max(0, (bigint & 255) - amount);
  
  return `${r}, ${g}, ${b}`;
}

/**
 * Lighten a hex color by a specified amount
 * Useful for light mode variations
 */
export function lightenColor(hex: string, amount: number = 30): string {
  if (!hex || typeof hex !== 'string') return '';
  
  const cleanHex = hex.replace('#', '');
  const fullHex = cleanHex.length === 3 
    ? cleanHex.split('').map(char => char + char).join('')
    : cleanHex;
  
  const bigint = parseInt(fullHex, 16);
  const r = Math.min(255, ((bigint >> 16) & 255) + amount);
  const g = Math.min(255, ((bigint >> 8) & 255) + amount);
  const b = Math.min(255, (bigint & 255) + amount);
  
  return `${r}, ${g}, ${b}`;
}

/**
 * Check if a color is considered "dark" (for accessibility)
 * Returns true if the color is dark, false if light
 */
export function isColorDark(hex: string): boolean {
  if (!hex || typeof hex !== 'string') return false;
  
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  
  const [r, g, b] = rgb.split(', ').map(Number);
  
  // Calculate luminance using standard formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance < 0.5;
}
